import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  ChevronDown,
  ChevronUp,
  X,
  MessageSquareText,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Download, // For Export
  Settings, // For Edit Dashboard
  Search,   // For Search
} from 'lucide-react';
// We assume these API functions are in '../services/api' and handle their own auth
import {
  fetch_chat_history,
  fetchCallAudio,
  fetchSessionHistory, // This now accepts our payload
  update_session_feedback,
} from '../services/api';
import jsonExport from 'jsonexport/dist'; // For CSV export: npm install jsonexport

// --- Reusable Star Component (from your file) ---
const Stars = ({ value = 0, onChange = null, editable = false, size = 5, ariaLabelPrefix = 'Rating' }) => {
  const stars = [];
  for (let i = 1; i <= size; i++) {
    const filled = i <= value;
    if (editable && typeof onChange === 'function') {
      stars.push(
        <button
          key={`star-${i}`} type="button"
          className={`px-1 text-lg leading-none focus:outline-none ${filled ? 'text-cyan-400' : 'text-gray-600'}`}
          onClick={() => onChange(i)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(i); } }}
          aria-label={`${ariaLabelPrefix} ${i} star${i > 1 ? 's' : ''}`} title={`${i} star${i > 1 ? 's' : ''}`}
        >
          {filled ? '★' : '☆'}
        </button>
      );
    } else {
      stars.push(<span key={`star-${i}`} className={`px-1 text-lg leading-none ${filled ? 'text-cyan-400' : 'text-gray-600'}`} aria-hidden="true" title={`${i} star${i > 1 ? 's' : ''}`} > {filled ? '★' : '☆'} </span>);
    }
  }
  return <div className="flex items-center">{stars}</div>;
};

// --- Helper to format Date object to 'yyyy-MM-dd' for input ---
const formatDateForInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

// --- Helper to format date for API (YYYY-MM-DD) ---
const formatDateForAPI = (date) => {
  if (!date) return null;
  let d = new Date(date);
  if (isNaN(d.getTime())) return null;
  const year = d.getUTCFullYear();
  const month = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = d.getUTCDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// --- Main Component ---
const ChatSessionViewer = () => {
  // --- State ---
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortDesc, setSortDesc] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState({});
  const [audioLoading, setAudioLoading] = useState({});
  const audioElementsRef = useRef({});
  const [currentPage, setCurrentPage] = useState(0); // This is 'index'
  const [sessionsPerPage, setSessionsPerPage] = useState(50); // State for Rows per page
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [editingAdminId, setEditingAdminId] = useState(null);
  const [adminEditValues, setAdminEditValues] = useState({ admin_rating: 0, admin_comments: '' });
  const [savingAdmin, setSavingAdmin] = useState({});
  const [visibleColumns, setVisibleColumns] = useState({
    time: true,
    summary: true,
    sentiment: true,
    lead: true,
    feedback: true,
    chat: true,
    callAudio: true,
  });
  const [isColumnEditorOpen, setIsColumnEditorOpen] = useState(false);
  // Local filters for API (dates only)
  const [localFilters, setLocalFilters] = useState({ start_date: '', end_date: '' });
  // Active filters for API (dates only)
  const [activeFilters, setActiveFilters] = useState({});
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [totalSessions, setTotalSessions] = useState(0);

  // **NEW** State for client-side search
  const [clientSearchInput, setClientSearchInput] = useState(''); // For the input box
  const [activeClientSearch, setActiveClientSearch] = useState(''); // For triggering the filter

  // **NEW** State for Go To Page input
  const [goToPageInput, setGoToPageInput] = useState('');

  // --- API Call & Auth Logic ---

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        index: currentPage,
        max_results: sessionsPerPage, // Use state variable
        sort_by: 'timestamp',
        order: sortDesc ? 'DESC' : 'ASC',
        start_date: formatDateForAPI(activeFilters.start_date),
        end_date: formatDateForAPI(activeFilters.end_date),
        // 'q' (API search) is REMOVED
      };
      Object.keys(payload).forEach(key => (payload[key] == null) && delete payload[key]);

      const sessionResponse = await fetchSessionHistory(payload);

      setTotalSessions(sessionResponse.total_sessions || 0);
      const sessionsRaw = sessionResponse.sessions || [];

      const processedSessions = sessionsRaw.map((s) => ({
        sessionId: s.session_id || 'No ID',
        type: s.type || 'Unknown',
        timestamp: s.timestamp ?? null,
        status: s.status || 'Unknown',
        customerName: s.customer_name || 'No name',
        agentName: s.agent_name || 'No agent',
        duration: s.duration || '0',
        userSentiment: s.user_sentiment || 'Unknown',
        callSummary: [s.call_summary, s.chat_summary].filter(Boolean).join('\n') || 'No summary available',
        user_rating: s.user_rating ?? '',
        user_comments: s.user_comments ?? '',
        admin_rating: s.admin_rating !== undefined && s.admin_rating !== null && s.admin_rating !== '' ? Number(s.admin_rating) : 0,
        admin_comments: s.admin_comments ?? '',
        leadInformation: s.lead_information || s.lead_info || s.leadInfo || null,
      }));

      setSessions(processedSessions);

    } catch (err) {
      setError('Failed to load sessions. Please try again later.');
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [activeFilters, currentPage, sortDesc, sessionsPerPage]);

  // Main data fetching effect
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- **UPDATED** Client-Side Search Logic ---
  const filteredSessions = useMemo(() => {
    if (!activeClientSearch) { // Depend on the *active* search term
      return sessions;
    }
    const lowerSearch = activeClientSearch.toLowerCase();
    return sessions.filter(s =>
      (s.callSummary && s.callSummary.toLowerCase().includes(lowerSearch)) ||
      (s.userSentiment && s.userSentiment.toLowerCase().includes(lowerSearch)) ||
      (s.type && s.type.toLowerCase().includes(lowerSearch)) ||
      (s.sessionId && s.sessionId.toLowerCase().includes(lowerSearch)) ||
      (s.leadInformation?.name && s.leadInformation.name.toLowerCase().includes(lowerSearch)) ||
      (s.leadInformation?.email && s.leadInformation.email.toLowerCase().includes(lowerSearch)) ||
      (s.leadInformation?.phone && s.leadInformation.phone.toLowerCase().includes(lowerSearch))
    );
  }, [sessions, activeClientSearch]); // Depend on activeClientSearch

  // --- Modal Body Scroll Lock ---
  useEffect(() => {
    const modalsOpen = isModalOpen || isSummaryModalOpen || isLeadModalOpen || isExportModalOpen;
    if (modalsOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isModalOpen, isSummaryModalOpen, isLeadModalOpen, isExportModalOpen]);


  // --- Original Handlers (Untouched) ---
  const handleOpenSummaryModal = (summary) => { setSelectedSummary(summary); setIsSummaryModalOpen(true); };
  const handleCloseSummaryModal = () => { setSelectedSummary(null); setIsSummaryModalOpen(false); };
  const handleOpenLeadModal = (lead) => { setSelectedLead(lead); setIsLeadModalOpen(true); };
  const handleCloseLeadModal = () => { setSelectedLead(null); setIsLeadModalOpen(false); };
  const toggleSort = () => { setSortDesc(!sortDesc); setCurrentPage(0); };
  const isButtonDisabled = (sessionId) => Object.entries(audioLoading).some(([id, isLoading]) => isLoading && id !== sessionId);
  const handleFetchCallAudio = async (sessionId) => { try { const audioResponse = await fetchCallAudio(sessionId); if (!audioResponse.ok) throw new Error(`Status: ${audioResponse.status}`); const contentType = audioResponse.headers.get('Content-Type') || ''; if (!contentType || !contentType.includes('audio')) throw new Error('Invalid content type.'); const audioBlob = await audioResponse.blob(); const audioUrl = URL.createObjectURL(audioBlob); const audio = new Audio(audioUrl); audio.addEventListener('ended', () => { setIsPlaying((prev) => ({ ...prev, [sessionId]: false, })); }); audioElementsRef.current[sessionId] = audio; } catch (error) { console.error('Error fetching call audio:', error); throw error; } };
  const togglePlayPause = async (sessionId) => { if (isButtonDisabled(sessionId) || audioLoading[sessionId]) return; try { if (!audioElementsRef.current[sessionId]) { setAudioLoading((prev) => ({ ...prev, [sessionId]: true })); await handleFetchCallAudio(sessionId); setAudioLoading((prev) => ({ ...prev, [sessionId]: false })); } if (!isPlaying[sessionId]) { setIsPlaying((prev) => { const newState = {}; Object.keys(prev).forEach((id) => { if (audioElementsRef.current[id]) { audioElementsRef.current[id].pause(); } newState[id] = false; }); newState[sessionId] = true; return newState; }); audioElementsRef.current[sessionId].play(); } else { audioElementsRef.current[sessionId].pause(); setIsPlaying((prev) => ({ ...prev, [sessionId]: false })); } } catch (error) { console.error(`Failed to play audio for session ${sessionId}:`, error); alert('Unable to play audio.'); setAudioLoading((prev) => ({ ...prev, [sessionId]: false })); } };
  const fetchChatHistory = async (sessionId) => { try { const chatResponse = await fetch_chat_history(sessionId); const chatData = await chatResponse.json(); if (!chatData || !Array.isArray(chatData.chat_history)) { console.error('Invalid chat data format:', chatData); return; } let chatHistory = []; if (chatData.chat_history.length > 0) { const firstEntry = chatData.chat_history[0]; if (typeof firstEntry.content === 'string') { try { chatHistory = JSON.parse(firstEntry.content); } catch (error) { chatHistory = chatData.chat_history; } } else if (Array.isArray(firstEntry.content)) { chatHistory = firstEntry.content; } else { chatHistory = chatData.chat_history; } } const processedHistory = chatHistory.map((msg) => ({ ...(msg.role === 'agent' && { agentMessage: msg.content }), ...(msg.role === 'user' && { userResponse: msg.content }), ...(msg.role !== 'agent' && msg.role !== 'user' && { otherMessage: msg.content || 'No content available' }), timestamp: msg.timestamp || 'No timestamp', })); setSelectedSession({ session_id: sessionId, type: chatData.type || 'unknown', history: processedHistory, }); setIsModalOpen(true); } catch (error) { console.error('Error fetching chat history:', error); } };
  const handleCloseModal = () => { setIsModalOpen(false); setSelectedSession(null); };
  const handleModalClick = (e) => { e.stopPropagation(); };
  const startAdminEdit = (session) => { setEditingAdminId(session.sessionId); setAdminEditValues({ admin_rating: session.admin_rating ? Number(session.admin_rating) : 0, admin_comments: session.admin_comments ?? '', }); };
  const cancelAdminEdit = () => { setEditingAdminId(null); setAdminEditValues({ admin_rating: 0, admin_comments: '' }); };
  const handleAdminInputChange = (field, value) => { setAdminEditValues((prev) => ({ ...prev, [field]: value })); };
  const handleStarClick = (value) => { setAdminEditValues((prev) => ({ ...prev, admin_rating: value })); };
  const updateAdminOnServer = async (sessionId, payload) => { const serverPayload = { session_id: sessionId, ...payload, }; try { const data = await update_session_feedback(serverPayload); return { ok: true, data }; } catch (err) { console.error('updateAdminOnServer error:', err); return { ok: false, error: err }; } };
  const saveAdminEdit = async (sessionId) => { if (!sessionId) return; setSavingAdmin((prev) => ({ ...prev, [sessionId]: true })); const payload = { admin_rating: Number(adminEditValues.admin_rating) || 0, admin_comments: adminEditValues.admin_comments, }; try { const resp = await updateAdminOnServer(sessionId, payload); if (!resp || resp.ok === false) { throw new Error('Failed to save admin edits to server'); } setSessions((prev) => prev.map((s) => (s.sessionId === sessionId ? { ...s, ...payload } : s))); setEditingAdminId(null); setAdminEditValues({ admin_rating: 0, admin_comments: '' }); } catch (err) { console.error('Failed to save admin edits:', err); alert('Unable to save edits. Please try again.'); } finally { setSavingAdmin((prev) => ({ ...prev, [sessionId]: false })); } };
  const formatTimeNoSeconds = (timestamp) => { if (!timestamp) return '—'; return new Date(timestamp).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', }); };
  const getSentimentEmoji = (sentiment) => { switch (sentiment) { case 'Positive': return '😊'; case 'Negative': return '😞'; case 'Neutral': return '😐'; default: return ''; } };

  // --- Handlers for New Features ---

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(0); // Reset to first page
    setActiveFilters(localFilters); // Trigger re-fetch
  };

  const handleResetFilters = () => {
    setLocalFilters({ start_date: '', end_date: '' });
    setActiveFilters({});
    setCurrentPage(0); // Reset to first page
  };

  const handleColumnToggle = (colName) => {
    setVisibleColumns(prev => ({ ...prev, [colName]: !prev[colName] }));
  };

  const handleOpenExportModal = () => {
    setIsExportModalOpen(true);
  };

  const handleCloseExportModal = () => {
    if (isExporting) return;
    setIsExportModalOpen(false);
  };

  // --- Rows Per Page Handler ---
  const handlePerPageChange = (event) => {
    setSessionsPerPage(Number(event.target.value));
    setCurrentPage(0); // Reset to page 0
  };

  // --- Page Change Handler ---
  const totalPages = Math.ceil(totalSessions / sessionsPerPage);
  const handlePageChange = (direction) => {
    let newPage = currentPage;
    if (direction === 'prev') {
      newPage = Math.max(0, currentPage - 1);
    } else if (direction === 'next') {
      if (currentPage + 1 >= totalPages) return; // Use totalPages
      newPage = currentPage + 1;
    } else if (typeof direction === 'number') {
      newPage = Math.max(0, Math.min(totalPages - 1, direction));
    }

    if (newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  };

  // --- Go To Page Handler ---
  const handleGoToPage = () => {
    const pageNum = parseInt(goToPageInput, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      handlePageChange(pageNum - 1); // Convert to 0-based index
      setGoToPageInput(''); // Clear input
    } else {
      alert(`Please enter a valid page number between 1 and ${totalPages}.`);
    }
  };

  // --- Key Down Handler for Go To Page ---
  const handleGoToPageKey = (e) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  // --- Export Logic ---
  const handleConfirmExport = async () => {
    setIsExporting(true);
    try {
      // 1. Fetch ALL records (no 'q' in payload)
      const payload = {
        start_date: formatDateForAPI(activeFilters.start_date),
        end_date: formatDateForAPI(activeFilters.end_date),
        index: 0,
        max_results: totalSessions > 0 ? totalSessions : 5000, // Fetch all
        sort_by: 'timestamp',
        order: sortDesc ? 'DESC' : 'ASC',
      };
      Object.keys(payload).forEach(key => (payload[key] == null) && delete payload[key]);

      const sessionResponse = await fetchSessionHistory(payload);
      const allRecords = sessionResponse.sessions || [];

      if (!allRecords || allRecords.length === 0) {
        alert('No data to export for the current selection.');
        setIsExporting(false);
        setIsExportModalOpen(false);
        return;
      }

      // 2. Process records and fetch chat histories
      const recordsWithDetails = await Promise.all(
        allRecords.map(async (record) => {
          const session = {
            sessionId: record.session_id || 'No ID', type: record.type || 'Unknown',
            timestamp: record.timestamp ?? null, callSummary: [record.call_summary, record.chat_summary].filter(Boolean).join('\n') || 'No summary available',
            userSentiment: record.user_sentiment || 'Unknown',
            leadInformation: record.lead_information || record.lead_info || record.leadInfo || null,
            user_rating: record.user_rating ?? '', admin_rating: record.admin_rating ? Number(record.admin_rating) : 0,
            admin_comments: record.admin_comments ?? '',
          };
          const exportedRecord = {};
          if (visibleColumns.time) exportedRecord['Time'] = session.timestamp ? formatTimeNoSeconds(session.timestamp) : '';
          if (visibleColumns.summary) exportedRecord['Summary'] = session.callSummary ?? '';
          if (visibleColumns.sentiment) exportedRecord['Sentiment'] = session.userSentiment ?? '';
          if (visibleColumns.lead) exportedRecord['Lead'] = session.leadInformation?.name ?? '';
          if (visibleColumns.feedback) {
            let feedbackString = "";
            if (session.admin_rating > 0) feedbackString += `${session.admin_rating} stars (Admin)`;
            if (session.admin_comments) feedbackString += `${feedbackString ? ' - ' : ''}"${session.admin_comments}"`;
            if (!feedbackString && session.user_rating > 0) feedbackString = `${session.user_rating} stars (User)`;
            exportedRecord['Feedback'] = feedbackString || 'N/A';
          }
          if (visibleColumns.chat) {
            let chatString = "N/A";
            if ((session.type === 'chat' || session.type === 'call') && session.sessionId) {
              try {
                const chatResponse = await fetch_chat_history(session.sessionId);
                const chatData = await chatResponse.json();
                let chatHistory = chatData.chat_history || [];
                if (chatHistory.length > 0 && typeof chatHistory[0].content === 'string') { try { chatHistory = JSON.parse(chatHistory[0].content); } catch (e) { } }
                if (!Array.isArray(chatHistory)) chatHistory = [];
                chatString = chatHistory.map(msg => `[${msg.role}] ${msg.content}`).join(' | ');
              } catch (e) { chatString = "Error fetching chat."; }
            }
            exportedRecord['ChatHistory'] = chatString;
          }
          return exportedRecord;
        })
      );

      jsonExport(recordsWithDetails, (err, csv) => {
        if (err) throw err;
        const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'history_export.csv');
        document.body.appendChild(link);
        link.click();
        setTimeout(() => { // Fix for Firefox
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 100);
        alert('Export complete!');
      });
    } catch (error) {
      console.error("Export failed:", error);
      alert(`Export failed: ${error.message}`);
    } finally {
      setIsExporting(false);
      setIsExportModalOpen(false);
    }
  };

  // --- UI Rendering ---

  // RENDER COLUMN EDITOR - Reverted back to standalone div
  const renderColumnEditor = () => (
    <div className="bg-[#1E293B] rounded-lg shadow-lg overflow-hidden my-4">
      <button
        className="w-full px-4 py-3 flex justify-between items-center bg-[#2C3E4C] hover:bg-[#334a60] transition-colors"
        onClick={() => setIsColumnEditorOpen(!isColumnEditorOpen)}
        aria-expanded={isColumnEditorOpen}
        aria-controls="edit-dashboard-content"
      >
        <h3 className="text-lg font-semibold text-white flex items-center">
          <Settings className="w-5 h-5 mr-2" /> Edit Dashboard
        </h3>
        {isColumnEditorOpen ? <ChevronUp className="w-5 h-5 text-gray-300" /> : <ChevronDown className="w-5 h-5 text-gray-300" />}
      </button>
      {isColumnEditorOpen && (
        <div id="edit-dashboard-content" className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 border-t border-gray-800">
          {Object.keys(visibleColumns).map(key => (
            <label key={key} className="flex items-center space-x-2 cursor-pointer text-gray-300 hover:text-white">
              <input
                type="checkbox"
                name={key}
                checked={visibleColumns[key]}
                onChange={() => handleColumnToggle(key)}
                className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-cyan-600 focus:ring-2 focus:ring-cyan-500 focus:outline-none"
              />
              <span className="capitalize text-sm">{
                { time: 'Time', summary: 'Summary', sentiment: 'Sentiment', lead: 'Lead', feedback: 'Feedback', chat: 'Details', callAudio: 'Audio' }[key] || key
              }</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );

  // --- Filter Bar (Dates Only) ---
  // MODIFIED: Export All button added to this bar.
  const renderFilterBar = () => (
    <div className="bg-[#1E293B] rounded-lg shadow-lg p-4 my-4 flex flex-wrap gap-4 items-end justify-between">
      {/* Left side: Date inputs and Apply/Reset buttons */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex flex-col">
          <label htmlFor="start_date" className="text-xs font-medium text-gray-400 mb-1">Start Date</label>
          <input
            type="date"
            id="start_date"
            name="start_date"
            value={localFilters.start_date}
            onChange={handleFilterChange}
            className="bg-[#111827] border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            style={{ colorScheme: 'dark' }}
          />
        </div>
        <div className="flex flex-col">
          <label htmlFor="end_date" className="text-xs font-medium text-gray-400 mb-1">End Date</label>
          <input
            type="date"
            id="end_date"
            name="end_date"
            value={localFilters.end_date}
            onChange={handleFilterChange}
            className="bg-[#111827] border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            style={{ colorScheme: 'dark' }}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition-colors h-full"
          >
            Apply Filters
          </button>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors h-full"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Right side: Export All button */}
      <div className="flex items-center">
        <button
          onClick={handleOpenExportModal}
          className="flex items-center space-x-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition-colors h-full"
        // Added ml-auto if this was inside a shared container, but with justify-between on the main div, this is correctly placed on the right.
        >
          <Download className="w-4 h-4" />
          <span>Export All</span>
        </button>
      </div>
    </div>
  );

  // --- **NEW** Client-Side Search Bar (with button) ---
  const renderClientSearch = () => (
    <div className="bg-[#1E293B] rounded-lg shadow-lg p-4 my-4 flex flex-wrap gap-4 items-end">
      <div className="flex flex-col flex-grow min-w-[250px]">
        <label htmlFor="client_search" className="text-xs font-medium text-gray-400 mb-1">Search in this page only</label>
        <div className="relative">
          <input
            type="text"
            id="client_search"
            value={clientSearchInput}
            onChange={(e) => setClientSearchInput(e.target.value)}
            placeholder="Search summary, sentiment, type, lead info..."
            className="bg-[#111827] border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 w-full pl-10 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setActiveClientSearch(clientSearchInput)} // Set the active search
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition-colors h-full"
        >
          Search Page
        </button>
        <button
          onClick={() => { setClientSearchInput(''); setActiveClientSearch(''); }} // Clear both
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors h-full"
        >
          Clear
        </button>
      </div>
    </div>
  );

  // --- Desktop View (Uses 'filteredSessions') ---
  const renderDesktopSessionView = () => {
    return (
      <div className="max-w-full mx-auto bg-[#1E293B] rounded-lg shadow-lg overflow-hidden hidden md:block">
        <div className="overflow-x-auto p-4">
          <table className="w-full">
            <thead className="bg-[#2C3E4C] border-b border-gray-800">
              <tr>
                {visibleColumns.time && <th className="px-6 py-4 text-left"> <button onClick={toggleSort} className="flex items-center space-x-2 text-gray-300 hover:text-cyan-400 transition-colors"> <span>Time</span> {sortDesc ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />} </button> </th>}
                {visibleColumns.summary && <th className="px-6 py-4 text-left text-gray-300">Summary</th>}
                {visibleColumns.sentiment && <th className="px-6 py-4 text-left text-gray-300">Sentiment</th>}
                {visibleColumns.lead && <th className="px-6 py-4 text-left text-gray-300">Lead</th>}
                {visibleColumns.feedback && <th className="px-6 py-4 text-left text-gray-300">Feedback</th>}
                {visibleColumns.chat && <th className="px-6 py-4 text-left text-gray-300">Chat</th>}
                {visibleColumns.callAudio && <th className="px-6 py-4 text-left text-gray-300">Call Audio</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {/* Use client-side filtered sessions */}
              {filteredSessions.map((session, index) => (
                <tr key={`${session.sessionId}-${index}`} className="hover:bg-[#2C3E4C] transition-colors">
                  {visibleColumns.time && <td className="px-6 py-4 text-gray-300">{formatTimeNoSeconds(session.timestamp)}</td>}
                  {visibleColumns.summary && <td className="px-6 py-4 text-gray-300"> <button onClick={() => handleOpenSummaryModal(session.callSummary)} className="text-left hover:text-cyan-400 transition-colors"> <div className="max-w-xs truncate" title="Click to view full summary"> {session.callSummary || '—'} </div> </button> </td>}
                  {visibleColumns.sentiment && <td className="px-6 py-4"> <div className="flex items-center space-x-2"> <span className={`px-2 py-1 rounded-full text-sm ${session.userSentiment === 'Positive' ? 'bg-green-500/20 text-green-400' : session.userSentiment === 'Negative' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}> {session.userSentiment || 'Unknown'} </span> <span className="text-xl" role="img" aria-label={`${session.userSentiment} sentiment`}> {getSentimentEmoji(session.userSentiment)} </span> </div> </td>}
                  {visibleColumns.lead && <td className="px-6 py-4 text-gray-300"> {session.leadInformation ? (<div className="flex items-center space-x-2"> <div className="text-sm truncate max-w-[180px]" title={`${session.leadInformation.name || '—'} — ${session.leadInformation.email || ''} — ${session.leadInformation.phone || ''}`}> {session.leadInformation.name || '—'} </div> <button onClick={() => handleOpenLeadModal(session.leadInformation)} className="p-1 rounded hover:bg-cyan-600/10 transition-colors" aria-label="View lead details" title="View lead details"> <ChevronRight className="w-4 h-4 text-cyan-400" /> </button> </div>) : (<span className="text-gray-400">—</span>)} </td>}
                  {visibleColumns.feedback && <td className="px-6 py-4 text-gray-300"> {editingAdminId === session.sessionId ? (<div className="space-y-2"> <div className="flex items-center space-x-3"> <Stars value={adminEditValues.admin_rating} onChange={(v) => handleStarClick(v)} editable={true} ariaLabelPrefix="Set rating to" /> </div> <textarea value={adminEditValues.admin_comments} onChange={(e) => handleAdminInputChange('admin_comments', e.target.value)} placeholder="Comments" rows={3} className="w-full px-3 py-2 bg-[#111827] border border-gray-700 rounded" /> <div className="flex items-center space-x-2"> <button onClick={() => saveAdminEdit(session.sessionId)} disabled={savingAdmin[session.sessionId]} className={`px-3 py-1 rounded ${savingAdmin[session.sessionId] ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-cyan-600 hover:bg-cyan-700 text-white'}`}> {savingAdmin[session.sessionId] ? 'Saving...' : 'Save'} </button> <button onClick={cancelAdminEdit} className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white"> Cancel </button> </div> </div>) : (<div className="flex items-start justify-between"> <div className="max-w-xs"> <div className="text-sm text-gray-300 flex items-center space-x-2 cursor-pointer" onClick={() => startAdminEdit(session)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') startAdminEdit(session); }} role="button" tabIndex={0} aria-label="Open feedback editor" title="Click to edit feedback" > <Stars value={session.admin_rating || session.user_rating || 0} editable={false} /> </div> <div className="text-xs text-gray-400 truncate mt-1" title={session.admin_comments || ''} onClick={() => startAdminEdit(session)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') startAdminEdit(session); }} role="button" tabIndex={0} style={{ cursor: 'pointer' }} > {session.admin_comments || '—'} </div> </div> </div>)} </td>}
                  {visibleColumns.chat && <td className="px-6 py-4"> <button onClick={() => fetchChatHistory(session.sessionId)} className="p-2 rounded-lg hover:bg-cyan-600/20 transition-colors" aria-label="View chat history"> <MessageSquareText className="w-5 h-5 text-cyan-400" /> </button> </td>}
                  {visibleColumns.callAudio && <td className="px-6 py-4"> {session.type === 'call' && (<button onClick={() => togglePlayPause(session.sessionId)} disabled={isButtonDisabled(session.sessionId)} className="p-2 rounded-lg hover:bg-cyan-600/20 transition-colors group" aria-label={audioLoading[session.sessionId] ? 'Loading audio' : isPlaying[session.sessionId] ? 'Pause audio' : 'Play audio'}> {audioLoading[session.sessionId] ? (<div className="h-5 w-5 border-2 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin" />) : isPlaying[session.sessionId] ? (<Pause className="w-5 h-5 text-cyan-400 group-hover:text-cyan-500" />) : (<Play className="w-5 h-5 text-cyan-400 group-hover:text-cyan-500" />)} </button>)} </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderMobileSessionView = () => {
    return (
      <div className="mt-4 px-4 space-y-4 md:hidden">
        {sessions.map((session, index) => (
          <div key={`${session.sessionId}-${index}`} className="bg-gradient-to-br from-[#2C3E4C] to-[#1E293B] rounded-lg shadow-lg p-4 space-y-3">
            {/* (Your original mobile card JSX) */}
          </div>
        ))}
      </div>
    );
  };

  // --- Modals (Summary, Lead, Chat - unchanged) ---
  const renderSummaryModal = () => { if (!isSummaryModalOpen || !selectedSummary) return null; return (<> <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" aria-hidden="true" onClick={handleCloseSummaryModal} /> <div className="fixed inset-0 flex items-center justify-center z-50 p-4" role="dialog"> <div className="bg-[#1E293B] rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col border border-gray-800 shadow-xl" onClick={(e) => e.stopPropagation()}> <div className="flex-shrink-0 p-4 border-b border-gray-800 bg-[#2C3E4C] flex justify-between items-center"> <h2 id="summary-modal-title" className="text-xl font-semibold text-white">Summary</h2> <button onClick={handleCloseSummaryModal} className="p-2 rounded-lg hover:bg-gray-700 transition-colors" aria-label="Close modal"> <X className="w-5 h-5 text-gray-400" /> </button> </div> <div className="flex-1 overflow-y-auto p-6"> <p className="text-gray-200 leading-relaxed whitespace-pre-wrap">{selectedSummary}</p> </div> </div> </div> </>); };
  const renderLeadModal = () => {
    if (!isLeadModalOpen || !selectedLead) return null;
    return (
      <>
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" aria-hidden="true" onClick={handleCloseLeadModal} />
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" role="dialog">
          <div className="bg-[#1E293B] rounded-lg max-w-md w-full max-h-[80vh] flex flex-col border border-gray-800 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex-shrink-0 p-4 border-b border-gray-800 bg-[#2C3E4C] flex justify-between items-center">
              <h2 id="lead-modal-title" className="text-xl font-semibold text-white">Lead Details</h2>
              <button onClick={handleCloseLeadModal} className="p-2 rounded-lg hover:bg-gray-700 transition-colors" aria-label="Close modal">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-3 text-gray-200">
                {Object.entries(selectedLead).map(([key, value]) => (
                  <div key={key}>
                    <div className="text-xs text-gray-400 uppercase font-bold tracking-wider">
                      {key.replace(/_/g, ' ')}
                    </div>
                    <div className="text-sm break-words">
                      {typeof value === 'object' ? JSON.stringify(value) : (value || '—')}
                    </div>
                  </div>
                ))}
                {Object.keys(selectedLead).length === 0 && (
                  <div className="text-gray-500 italic">No lead details available.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };
  const renderChatModal = () => { if (!isModalOpen || !selectedSession) return null; return (<> <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" aria-hidden="true" onClick={handleCloseModal} /> <div className="fixed inset-0 flex items-center justify-center z-50 p-4" role="dialog"> <div className="bg-[#1E293B] rounded-lg max-w-2xl w-full h-[80vh] flex flex-col border border-gray-800 shadow-xl" onClick={handleModalClick}> <div className="flex-shrink-0 p-4 border-b border-gray-800 bg-[#2C3E4C] flex justify-between items-center"> <h2 id="modal-title" className="text-xl font-semibold text-white">Chat History</h2> <button onClick={handleCloseModal} className="p-2 rounded-lg hover:bg-gray-700 transition-colors" aria-label="Close modal"> <X className="w-5 h-5 text-gray-400" /> </button> </div> <div className="flex-1 overflow-y-auto p-4"> <div className="space-y-4"> {selectedSession.history?.map((msg, index) => (<div key={`msg-${selectedSession.session_id}-${index}`} className="space-y-4"> <> {msg.agentMessage && (<div className="bg-[#2C3E4C] border border-gray-800 p-4 rounded-lg"> <div className="flex items-start gap-3"> <div className="flex-shrink-0 h-8 w-8 rounded-lg bg-cyan-500/20 flex items-center justify-center"> <span className="text-cyan-400 text-sm font-semibold">A</span> </div> <div className="flex-1"> <p className="text-sm text-gray-400 mb-1">Agent</p> <p className="text-gray-200 whitespace-pre-wrap">{msg.agentMessage}</p> </div> </div> </div>)} {msg.userResponse && (<div className="bg-cyan-600/20 border border-cyan-500/20 p-4 rounded-lg ml-4"> <div className="flex items-start gap-3"> <div className="flex-shrink-0 h-8 w-8 rounded-lg bg-cyan-500/20 flex items-center justify-center"> <span className="text-cyan-400 text-sm font-semibold">U</span> </div> <div className="flex-1"> <p className="text-sm text-gray-400 mb-1">User</p> <p className="text-gray-200 whitespace-pre-wrap">{msg.userResponse}</p> </div> </div> </div>)} {msg.otherMessage && (<div className="bg-gray-700 border border-gray-800 p-4 rounded-lg"> <p className="text-gray-400 text-sm">{msg.otherMessage}</p> </div>)} </> </div>))} </div> </div> </div> </div> </>) };

  // --- Export Confirmation Modal ---
  const renderExportModal = () => (
    <>
      {isExportModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" aria-hidden="true" onClick={handleCloseExportModal} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="export-modal-title">
            <div className="bg-[#1E293B] rounded-lg max-w-md w-full flex flex-col border border-gray-800 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex-shrink-0 p-4 border-b border-gray-800 bg-[#2C3E4C] flex justify-between items-center">
                <h2 id="export-modal-title" className="text-xl font-semibold text-white">Confirm Export</h2>
                <button onClick={handleCloseExportModal} disabled={isExporting} className="p-2 rounded-lg hover:bg-gray-700 transition-colors" aria-label="Close modal">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-300 mb-4">
                  Do you want to export all **{totalSessions}** records matching your current selection?
                </p>
                <div className="flex justify-end space-x-2">
                  <button onClick={handleCloseExportModal} disabled={isExporting} className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium transition-colors disabled:opacity-50">
                    Cancel
                  </button>
                  <button onClick={handleConfirmExport} disabled={isExporting} className={`px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium transition-colors flex items-center ${isExporting ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    {isExporting && <div className="h-4 w-4 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2" />}
                    {isExporting ? 'Exporting...' : 'Export'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );

  // --- Pagination Controls ---
  const renderPaginationControls = () => {
    const totalPages = Math.ceil(totalSessions / sessionsPerPage);
    if (totalPages <= 1 && sessions.length === 0) return null; // No pagination if 0 results
    if (totalPages <= 1 && sessions.length > 0) return (
      <div className="flex justify-center p-4 border-t border-gray-800 mt-4 bg-[#1E293B] rounded-b-lg">
        <span className="text-sm text-gray-300">1–{sessions.length} of {totalSessions}</span>
      </div>
    );


    const pageNumbers = [];
    const maxPagesToShow = 5; // Show 5 numbers (e.g., 1, 2, 3, 4, 5)

    let startPage = Math.max(0, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    const startEntry = totalSessions === 0 ? 0 : (currentPage * sessionsPerPage) + 1;
    const endEntry = Math.min((currentPage + 1) * sessionsPerPage, totalSessions);

    const handleGoToPage = () => {
      const pageNum = parseInt(goToPageInput, 10);
      if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
        handlePageChange(pageNum - 1); // Convert to 0-based index
        setGoToPageInput(''); // Clear input
      } else {
        alert(`Please enter a valid page number between 1 and ${totalPages}.`);
      }
    };

    const handleGoToPageKey = (e) => {
      if (e.key === 'Enter') {
        handleGoToPage();
      }
    };

    return (
      <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 p-4 border-t border-gray-800 mt-4 bg-[#1E293B] rounded-b-lg">
        {/* Rows per page */}
        <div className="flex items-center space-x-2 text-sm text-gray-300">
          <span>Rows per page:</span>
          <select
            value={sessionsPerPage}
            onChange={handlePerPageChange}
            className="bg-[#111827] border border-gray-700 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        {/* Entry count */}
        <span className="text-sm text-gray-300">
          {startEntry}–{endEntry} of {totalSessions}
        </span>

        {/* Page number controls */}
        <div className="flex items-center space-x-1">
          <button
            onClick={() => handlePageChange('prev')}
            disabled={currentPage === 0}
            className={`p-2 rounded-lg ${currentPage === 0 ? 'text-gray-500 cursor-not-allowed' : 'text-white hover:bg-cyan-600/20'} transition-colors`}
            aria-label="Previous page"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {startPage > 0 && (
            <>
              <button
                onClick={() => handlePageChange(0)}
                className={`px-3 py-1 rounded-lg ${currentPage === 0 ? 'bg-cyan-600' : 'bg-gray-700 hover:bg-gray-600'} text-white text-sm`}
              >
                1
              </button>
              {startPage > 1 && <span className="px-2 text-gray-500">...</span>}
            </>
          )}

          {pageNumbers.map(number => (
            <button
              key={number}
              onClick={() => handlePageChange(number)}
              className={`px-3 py-1 rounded-lg ${currentPage === number ? 'bg-cyan-600' : 'bg-gray-700 hover:bg-gray-600'} text-white text-sm`}
            >
              {number + 1}
            </button>
          ))}

          {endPage < totalPages - 1 && (
            <>
              {endPage < totalPages - 2 && <span className="px-2 text-gray-500">...</span>}
              <button
                onClick={() => handlePageChange(totalPages - 1)}
                className={`px-3 py-1 rounded-lg ${currentPage === totalPages - 1 ? 'bg-cyan-600' : 'bg-gray-700 hover:bg-gray-600'} text-white text-sm`}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => handlePageChange('next')}
            disabled={currentPage + 1 >= totalPages}
            className={`p-2 rounded-lg ${currentPage + 1 >= totalPages ? 'text-gray-500 cursor-not-allowed' : 'text-white hover:bg-cyan-600/20'} transition-colors`}
            aria-label="Next page"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Go to Page Input */}
        <div className="hidden md:flex items-center space-x-2 text-sm text-gray-300">
          <span>Go to page:</span>
          <input
            type="text"
            value={goToPageInput}
            onChange={(e) => setGoToPageInput(e.target.value.replace(/\D/g, ''))} // Only allow numbers
            onKeyDown={handleGoToPageKey}
            className="w-16 bg-[#111827] border border-gray-700 rounded px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <button
            onClick={handleGoToPage}
            className="px-3 py-1 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm"
          >
            Go
          </button>
        </div>
      </div>
    );
  };


  // --- Main Return ---
  return (
    <div className="min-h-screen w-full flex flex-col bg-[#121721] text-gray-100">
      <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-[95%] mx-auto">

          {/* 1. Edit Dashboard Accordion */}
          {renderColumnEditor()}

          {/* 2. Filter Bar (Dates Only) - Now includes Export All */}
          {renderFilterBar()}

          {/* 3. Client-Side Search Bar */}
          {renderClientSearch()}
        </div>

        {/* 4. Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="h-8 w-8 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 text-red-400 space-y-4 bg-[#1E293B] rounded-lg p-8">
            <p className="font-semibold">{error}</p>
            <button onClick={fetchData} className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors">
              Retry
            </button>
          </div>
        ) : (
          <div className="max-w-[95%] mx-auto">

            {/* Show No Results for client-side search */}
            {sessions.length > 0 && filteredSessions.length === 0 && (
              <div className="text-center text-gray-400 py-8 bg-[#1E293B] rounded-lg">
                <p>No results match your search on this page.</p>
              </div>
            )}

            {/* Show No Results for API fetch */}
            {sessions.length === 0 && (
              <div className="text-center text-gray-400 py-8 bg-[#1E293B] rounded-lg">
                <p>No history found for the selected filters.</p>
              </div>
            )}

            {/* Show data if it exists */}
            {filteredSessions.length > 0 && renderDesktopSessionView()}
            {filteredSessions.length > 0 && renderMobileSessionView()}

            {/* Modals */}
            {renderSummaryModal()}
            {renderLeadModal()}
            {renderChatModal()}
            {renderExportModal()}

            {/* Pagination */}
            {renderPaginationControls()}
          </div>
        )}
      </main>
    </div>
  );
};

export default ChatSessionViewer;